// activeSafetyForms/nearMiss/actions.js

import { NearMiss } from "../../../models/SafetyForms/nearMiss/NearMiss";
import {
  createNearMissFormDraft,
  getNearMissFormInfo,
} from "../../../api/SafetyFormApi";
import { activeNearMissInitialState } from "./activeSafetyFormsNearMissState";
import { nearMissFormInitialState } from "../../forms/nearMissForm/nearMissFormState";
import { makeNearMissFormActionsForTab } from "../../forms/nearMissForm/nearMissFormActions";

export const makeActiveNearMissActions = (set, get) => ({
  // lifecycle
  startNewNearMiss: async (user) => {
    // Build local model immediately for UX
    const nearMissModel = new NearMiss();

    // Create on server to get a persistent near_miss_id
    try {
      const response = await createNearMissFormDraft(user);
      const { near_miss_id, creation_date } = response.data || {};
      if (near_miss_id) {
        console.log("Near Miss ID", near_miss_id);
        nearMissModel.updateNearMissId(near_miss_id);
        nearMissModel.created_at = creation_date;
      }
    } catch (e) {
      // If create failed, proceed with local model; first autosave will create
      console.error("Error creating near miss draft:", e);
    }

    set((s) => ({
      activeNearMiss: {
        ...s.activeNearMiss,
        currentNearMiss: nearMissModel,
        nearMissMode: "new",
        nearMissErrors: null,
        rev: 0,
        nearMissForm: nearMissFormInitialState,
      },
    }));

    // Upsert minimal row into Near Miss list
    try {
      const row = {
        id: nearMissModel.id,
        created_at:
          nearMissModel.created_at || new Date().toISOString().split("T")[0],
        updated_at:
          nearMissModel.created_at || new Date().toISOString().split("T")[0],
        incident_date: nearMissModel.incident_date || "",
        incident_time: nearMissModel.incident_time || "",
        location: nearMissModel.location || "",
        site: nearMissModel.site || "",
        reported_to: nearMissModel.reported_to_name || "",
        reported_by: nearMissModel.reported_by_name || "",
      };
      get().safetyFormListsActions.upsertNearMissDraftLocal(row);
    } catch (err) {
      console.error("Error upserting near miss to list:", err);
    }
  },

  fetchNearMiss: async (nearMissMode, nearMissId) => {
    set((s) => ({
      activeNearMiss: { ...s.activeNearMiss, isLoading: true, error: null },
    }));

    try {
      const response = await getNearMissFormInfo(nearMissId);
      console.log("fetchNearMiss response:", response);

      // Backend returns {success: ..., data: near_miss_info}
      if (!response.data || response.data === false) {
        set((s) => ({
          activeNearMiss: {
            ...s.activeNearMiss,
            isLoading: false,
            error: "Near Miss not found or could not be loaded.",
            currentNearMiss: null,
            nearMissMode: null,
            rev: 0,
          },
        }));
        return {
          ok: false,
          error: "Near Miss not found or could not be loaded.",
        };
      }

      const nearMissModel = new NearMiss(response.data);
      const effectiveMode = nearMissMode === "copy" ? "edit" : nearMissMode;

      set((s) => ({
        activeNearMiss: {
          ...s.activeNearMiss,
          isLoading: false,
          currentNearMiss: nearMissModel,
          nearMissMode: effectiveMode,
          nearMissErrors: null,
          rev: 0,
          nearMissForm: nearMissFormInitialState,
        },
      }));

      // If this was a copy, ensure the new Near Miss appears in the list immediately
      if (nearMissMode === "copy") {
        try {
          const row = {
            id: nearMissModel.id,
            creation_date:
              nearMissModel.created_at ||
              new Date().toISOString().split("T")[0],
            updated_date: new Date().toISOString().split("T")[0],
            incident_date: nearMissModel.incident_date || "",
            incident_time: nearMissModel.incident_time || "",
            location: nearMissModel.location || "",
            site: nearMissModel.site || "",
            reported_to: nearMissModel.reported_to_name || "",
            reported_by: nearMissModel.reported_by_name || "",
          };
          get().safetyFormListsActions.upsertNearMissDraftLocal(row);
        } catch (err) {
          console.error("Error upserting copied near miss:", err);
        }
      }

      return { ok: true };
    } catch (err) {
      set((s) => ({
        activeNearMiss: {
          ...s.activeNearMiss,
          isLoading: false,
          error: err,
          currentNearMiss: null,
          nearMissMode: null,
          rev: 0,
        },
      }));
      return { ok: false, error: err };
    }
  },

  // Hydrate the active Near Miss directly from a payload (e.g., copy/new endpoints)
  hydrateNearMissFromPayload: (nearMissMode, nearMissPayload) => {
    try {
      const nearMissModel = new NearMiss(nearMissPayload);

      set((s) => ({
        activeNearMiss: {
          ...s.activeNearMiss,
          isLoading: false,
          currentNearMiss: nearMissModel,
          nearMissMode: nearMissMode === "copy" ? "edit" : nearMissMode,
          nearMissErrors: null,
          rev: 0,
          nearMissForm: nearMissFormInitialState,
        },
      }));

      return { ok: true, id: nearMissModel.id };
    } catch (err) {
      set((s) => ({
        activeNearMiss: {
          ...s.activeNearMiss,
          isLoading: false,
          error: err,
        },
      }));
      return { ok: false, error: err };
    }
  },

  clearNearMiss: () =>
    set((s) => ({
      activeNearMiss: {
        ...activeNearMissInitialState,
        // keep tab identity
        tabName: "nearMiss",
        rev: 0,
      },
    })),

  // UI helpers
  setNearMissErrors: (errors) =>
    set((s) => ({
      activeNearMiss: { ...s.activeNearMiss, nearMissErrors: errors },
    })),

  nearMissFormActions: Object.freeze(
    makeNearMissFormActionsForTab(set, get, "activeNearMiss")
  ),
});
